<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

const { t } = useI18n();
const router = useRouter();
const { accountId, currentAccount } = useAccount();

const globalConfig = useMapGetter('globalConfig/get');
const enabledFeatures = ref({});

const channelList = computed(() => {
  const { apiChannelName } = globalConfig.value;
  return [
    {
      key: 'website',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.DESCRIPTION'),
      icon: 'i-ph-globe-bold',
    },
    {
      key: 'facebook',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.DESCRIPTION'),
      icon: 'i-ph-messenger-logo-bold',
    },
    {
      key: 'whatsapp',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.DESCRIPTION'),
      icon: 'i-ph-whatsapp-logo-bold',
    },
    {
      key: 'sms',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.SMS.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.SMS.DESCRIPTION'),
      icon: 'i-ph-chat-dots-bold',
    },
    {
      key: 'email',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.DESCRIPTION'),
      icon: 'i-ph-envelope-simple-bold',
    },
    {
      key: 'api',
      title: apiChannelName || t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.DESCRIPTION'),
      icon: 'i-ph-code-bold',
    },
    {
      key: 'telegram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.DESCRIPTION'),
      icon: 'i-ph-telegram-logo-bold',
    },
    {
      key: 'line',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LINE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LINE.DESCRIPTION'),
      icon: 'i-ph-chat-circle-bold',
    },
    {
      key: 'instagram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.DESCRIPTION'),
      icon: 'i-ph-instagram-logo-bold',
    },
    {
      key: 'voice',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VOICE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VOICE.DESCRIPTION'),
      icon: 'i-ph-phone-bold',
    },
  ];
});

const initializeEnabledFeatures = async () => {
  enabledFeatures.value = currentAccount.value.features;
};

const initChannelAuth = channel => {
  const params = {
    sub_page: channel,
    accountId: accountId.value,
  };
  router.push({ name: 'settings_inboxes_page_channel', params });
};

const goBack = () => {
  router.push({ name: 'settings_inbox_list' });
};

onMounted(() => {
  initializeEnabledFeatures();
});
</script>

<template>
  <div class="channel-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header class="channel-header">
      <div class="px-8 py-6">
        <button class="channel-back-btn" @click="goBack">
          <span class="i-ph-arrow-left-bold size-4" />
          Back to Inboxes
        </button>
        <div class="flex items-center gap-3 mb-2">
          <div class="header-icon-wrap">
            <span class="i-ph-plus-circle-bold text-slate-700 size-5" />
          </div>
          <h1 class="text-2xl font-bold text-slate-800">Create New Inbox</h1>
        </div>
        <p class="channel-header-desc text-sm max-w-xl">
          Choose a channel to connect with your customers. Each channel creates
          a unique inbox for your team.
        </p>
      </div>
    </header>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-8">
      <div class="max-w-5xl">
        <h2
          class="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-6"
        >
          Available Channels
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            v-for="channel in channelList"
            :key="channel.key"
            class="channel-card group"
            @click="initChannelAuth(channel.key)"
          >
            <div class="flex items-start gap-4">
              <div class="channel-icon">
                <span class="size-5 text-slate-600" :class="channel.icon" />
              </div>
              <div class="flex-1 text-left">
                <h3 class="text-base font-semibold text-slate-800 mb-1">
                  {{ channel.title }}
                </h3>
                <p class="text-xs text-slate-500 leading-relaxed line-clamp-2">
                  {{ channel.description }}
                </p>
              </div>
            </div>
            <div class="channel-arrow">
              <span class="i-ph-arrow-right-bold size-4" />
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.channel-page,
.dark .channel-page {
  background-color: #ffffff !important;
}

.channel-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb;
}

.channel-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 1rem;
  transition: color 0.15s ease;
}

.channel-back-btn:hover {
  color: #111827;
}

.channel-header-desc {
  color: #64748b !important;
}

.header-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.channel-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 120px;
}

.channel-card:hover {
  border-color: #cbd5e1;
  background: #ffffff;
  transform: translateY(-2px);
  box-shadow:
    0 10px 20px rgba(15, 23, 42, 0.08),
    0 4px 8px rgba(15, 23, 42, 0.04);
}

.channel-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s ease;
}

.channel-card:hover .channel-icon {
  background: #e9ebed;
}

.channel-arrow {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: #94a3b8;
  margin-top: 12px;
  transition: all 0.2s ease;
}

.channel-card:hover .channel-arrow {
  color: #334155;
  transform: translateX(4px);
}

.dark .channel-header,
.dark .channel-card {
  background: #ffffff !important;
}

.dark .channel-card {
  border-color: #e5e7eb !important;
}

.dark .channel-card .text-n-slate-11,
.dark .channel-page .text-n-slate-11 {
  color: #64748b !important;
}
</style>
